import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { locationAPI } from '../services/api';
import { toast } from 'react-toastify';
import Map from '../components/Map';
import { FiList, FiPlus, FiMapPin, FiStar, FiEdit2 } from 'react-icons/fi';
import './LocationsMap.css';

const LocationsMap = () => {
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await locationAPI.getAll();
      setLocations(response.data.locations || []);
    } catch (error) {
      toast.error('Failed to fetch locations');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkerClick = (location) => {
    setSelectedLocation(location);
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  return (
    <div className="locations-map-page">
      <div className="container">
        <div className="page-header">
          <div>
            <h1><FiMapPin /> Locations Map</h1>
            <p>View all your saved delivery locations</p>
          </div>
          <div className="header-actions">
            <Link to="/locations" className="btn btn-outline">
              <FiList /> List View
            </Link>
            <Link to="/locations/new" className="btn btn-primary">
              <FiPlus /> Add Location
            </Link>
          </div>
        </div>

        {locations.length === 0 ? (
          <div className="empty-state card">
            <FiMapPin className="empty-icon" />
            <h3>No locations saved yet</h3>
            <p>Add your first location to see it on the map</p>
            <Link to="/locations/new" className="btn btn-primary">
              <FiPlus /> Add Your First Location
            </Link>
          </div>
        ) : (
          <div className="map-container">
            <div className="map-wrapper card">
              <Map
                locations={locations}
                height="500px"
                zoom={12}
                onMarkerClick={handleMarkerClick}
              />
            </div>

            {selectedLocation && (
              <div className="location-detail card">
                <div className="detail-header">
                  <h2>{selectedLocation.label}</h2>
                  {selectedLocation.isDefault && (
                    <span className="default-badge"><FiStar /> Default</span>
                  )}
                </div>

                <div className="detail-address">
                  <p>{selectedLocation.address?.full}</p>
                  {selectedLocation.address?.flatNumber && (
                    <p className="flat-info">
                      {selectedLocation.address.building && `${selectedLocation.address.building}, `}
                      {selectedLocation.address.flatNumber}
                      {selectedLocation.address.floor && `, ${selectedLocation.address.floor}`}
                    </p>
                  )}
                </div>

                {selectedLocation.deliveryInstructions?.directions && (
                  <div className="detail-instructions">
                    <strong>Delivery Instructions:</strong>
                    <p>{selectedLocation.deliveryInstructions.directions}</p>
                  </div>
                )}

                {selectedLocation.gateSystem?.type && selectedLocation.gateSystem.type !== 'none' && (
                  <div className="detail-gate">
                    <span className="gate-badge">{selectedLocation.gateSystem.type}</span>
                  </div>
                )}

                <div className="detail-actions">
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate(`/locations/${selectedLocation._id}/edit`)}
                  >
                    <FiEdit2 /> Edit Location
                  </button>
                </div>
              </div>
            )}

            <div className="locations-list-mini">
              <h3>All Locations ({locations.length})</h3>
              <div className="mini-list">
                {locations.map(loc => (
                  <div
                    key={loc._id}
                    className={`mini-item ${selectedLocation?._id === loc._id ? 'active' : ''}`}
                    onClick={() => setSelectedLocation(loc)}
                  >
                    <div className={`type-indicator ${loc.type}`} />
                    <div className="mini-info">
                      <span className="mini-label">{loc.label}</span>
                      <span className="mini-address">{loc.address?.full?.substring(0, 40)}...</span>
                    </div>
                    {loc.isDefault && <FiStar className="default-icon" />}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationsMap;
